import { useState, useEffect, useCallback } from 'react'

const EMPTY_FILTERS = {
  created_from: null,
  created_to: null,
  include_ic: [],
  exclude_ic: [],
  include_flag: [],
  exclude_flag: [],
  exclude_campaign_id: [],
  agent: null,
  search: '',
}

function parseUrlParams(searchString) {
  const params = new URLSearchParams(searchString)
  const f = { ...EMPTY_FILTERS }
  if (params.has('from')) f.created_from = params.get('from')
  if (params.has('to')) f.created_to = params.get('to')
  if (params.has('inc_ic')) f.include_ic = params.get('inc_ic').split(',').filter(Boolean)
  if (params.has('exc_ic')) f.exclude_ic = params.get('exc_ic').split(',').filter(Boolean)
  if (params.has('inc_flag')) f.include_flag = params.get('inc_flag').split(',').filter(Boolean)
  if (params.has('exc_flag')) f.exclude_flag = params.get('exc_flag').split(',').filter(Boolean)
  if (params.has('exc_camp')) f.exclude_campaign_id = params.get('exc_camp').split(',').filter(Boolean)
  if (params.has('agent')) f.agent = params.get('agent')
  if (params.has('q')) f.search = params.get('q')
  return f
}

function filtersToUrlParams(filters) {
  const params = new URLSearchParams()
  if (filters.created_from) params.set('from', filters.created_from)
  if (filters.created_to) params.set('to', filters.created_to)
  if (filters.include_ic?.length) params.set('inc_ic', filters.include_ic.join(','))
  if (filters.exclude_ic?.length) params.set('exc_ic', filters.exclude_ic.join(','))
  if (filters.include_flag?.length) params.set('inc_flag', filters.include_flag.join(','))
  if (filters.exclude_flag?.length) params.set('exc_flag', filters.exclude_flag.join(','))
  if (filters.exclude_campaign_id?.length) params.set('exc_camp', filters.exclude_campaign_id.join(','))
  if (filters.agent) params.set('agent', filters.agent)
  if (filters.search) params.set('q', filters.search)
  return params.toString()
}

function hasAnyFilter(filters) {
  return !!(
    filters.created_from || filters.created_to
    || filters.include_ic?.length || filters.exclude_ic?.length
    || filters.include_flag?.length || filters.exclude_flag?.length
    || filters.exclude_campaign_id?.length
    || filters.agent || filters.search
  )
}

// Deriva las "chip keys" presentes a partir de los filtros que ya tienen valor.
function deriveKeys(f) {
  const k = []
  if (f.created_from || f.created_to) k.push('date')
  if (f.include_ic?.length) k.push('include_ic')
  if (f.exclude_ic?.length) k.push('exclude_ic')
  if (f.include_flag?.length) k.push('include_flag')
  if (f.exclude_flag?.length) k.push('exclude_flag')
  if (f.exclude_campaign_id?.length) k.push('exclude_camp')
  if (f.agent) k.push('agent')
  return k
}

/**
 * Hook que sincroniza estado de filtros con URL params y localStorage por agente.
 * Mount: URL > localStorage > EMPTY_FILTERS.
 * Change: debounce 300ms -> write to URL (replaceState) + localStorage.
 *
 * Ademas maneja "presentKeys": que chips se muestran, separado de sus valores,
 * para que un filtro recien agregado (aun vacio) muestre su chip y abra su selector.
 *
 * @param {string} agentName - Nombre canonical del agente (para namespace del localStorage)
 */
export default function useCampaignBuilderFilters(agentName) {
  const storageKey = `crm_campaign_builder_filters_${agentName || 'anon'}`

  const [filters, setFilters] = useState(() => {
    if (typeof window === 'undefined') return EMPTY_FILTERS
    const fromUrl = parseUrlParams(window.location.search)
    if (hasAnyFilter(fromUrl)) return fromUrl
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) return { ...EMPTY_FILTERS, ...JSON.parse(stored) }
    } catch {}
    return EMPTY_FILTERS
  })

  // Chips presentes (estado UI), derivados al montar de los filtros con valor.
  const [presentKeys, setPresentKeys] = useState(() => deriveKeys(filters))
  // Key recien agregada -> su chip abre el selector automaticamente.
  const [justAddedKey, setJustAddedKey] = useState(null)

  // Debounced write to URL + localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return
    const timer = setTimeout(() => {
      const qs = filtersToUrlParams(filters)
      const newUrl = `${window.location.pathname}${qs ? '?' + qs : ''}`
      const currentUrl = window.location.pathname + window.location.search
      if (newUrl !== currentUrl) {
        window.history.replaceState(null, '', newUrl)
      }
      try {
        localStorage.setItem(storageKey, JSON.stringify(filters))
      } catch {}
    }, 300)
    return () => clearTimeout(timer)
  }, [filters, storageKey])

  // Back/forward navigation: re-derivar presentKeys de la URL
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onPop = () => {
      const f = parseUrlParams(window.location.search)
      setFilters(f)
      setPresentKeys(deriveKeys(f))
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [])

  const addFilterKey = useCallback((chipKey) => {
    setPresentKeys(prev => prev.includes(chipKey) ? prev : [...prev, chipKey])
    setJustAddedKey(chipKey)
  }, [])

  const removeFilterKey = useCallback((chipKey) => {
    setPresentKeys(prev => prev.filter(k => k !== chipKey))
    setFilters(prev => {
      const next = { ...prev }
      switch (chipKey) {
        case 'date': next.created_from = null; next.created_to = null; break
        case 'include_ic': next.include_ic = []; break
        case 'exclude_ic': next.exclude_ic = []; break
        case 'include_flag': next.include_flag = []; break
        case 'exclude_flag': next.exclude_flag = []; break
        case 'exclude_camp': next.exclude_campaign_id = []; break
        case 'agent': next.agent = null; break
        default: break
      }
      return next
    })
  }, [])

  const clearJustAdded = useCallback(() => setJustAddedKey(null), [])

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS)
    setPresentKeys([])
    setJustAddedKey(null)
  }, [])

  return {
    filters, setFilters, updateFilter, resetFilters,
    presentKeys, addFilterKey, removeFilterKey, justAddedKey, clearJustAdded,
  }
}
